import { useState, useEffect } from 'react';
import {
  Building, Building2, Search, Plus, Pencil, Trash2, RotateCcw,
  Loader2, Phone, Mail, MapPin, X, ChevronDown, ChevronRight, Tag, MapPinned
} from 'lucide-react';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import Button from '../../../components/ui/Button';
import AddressInput from '../../../components/ui/AddressInput';
import { cn } from '../../../utils/cn';
import { useCompany } from '../../../contexts/CompanyContext';
import { clientService } from '../../../services/clientService';
import { jobSiteService } from '../../../services/jobSiteService';

/**
 * Clients Management Page
 * Hierarchical view: Clients → Job Sites (expandable)
 */
export default function ClientsPage() {
  const { currentCompany } = useCompany();

  // Data states
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('client'); // 'client' or 'jobSite'
  const [editingItem, setEditingItem] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null); // For adding job site

  // Expansion state
  const [expandedClients, setExpandedClients] = useState(new Set());

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: null, // 'client' or 'jobSite'
    item: null
  });

  // Load data
  useEffect(() => {
    if (currentCompany?.id) {
      loadData();
    }
  }, [currentCompany?.id, showInactive]);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await clientService.getClientsWithJobSites(currentCompany.id, showInactive);
      setClients(data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleClient = (clientId) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  const handleAddClient = () => {
    setEditingItem(null);
    setModalType('client');
    setSelectedClientId(null);
    setShowModal(true);
  };

  const handleEditClient = (client) => {
    setEditingItem(client);
    setModalType('client');
    setShowModal(true);
  };

  const handleAddJobSite = (clientId) => {
    setEditingItem(null);
    setModalType('jobSite');
    setSelectedClientId(clientId);
    setShowModal(true);
  };

  const handleEditJobSite = (jobSite, clientId) => {
    setEditingItem(jobSite);
    setModalType('jobSite');
    setSelectedClientId(clientId);
    setShowModal(true);
  };

  const handleDeleteClient = (client) => {
    setDeleteModal({
      isOpen: true,
      type: 'client',
      item: client
    });
  };

  const handleDeactivateClient = async (client) => {
    try {
      await clientService.deactivateClient(client.id);
      loadData();
    } catch (err) {
      console.error('Failed to deactivate:', err);
      alert('Failed to deactivate. Please try again.');
    }
  };

  const handlePermanentDeleteClient = async (client) => {
    try {
      await clientService.deleteClient(client.id);
      loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
      alert(err?.message || 'Failed to delete. Please try again.');
    }
  };

  const handleReactivateClient = async (client) => {
    try {
      await clientService.reactivateClient(client.id);
      loadData();
    } catch (err) {
      console.error('Failed to reactivate:', err);
      alert('Failed to reactivate. Please try again.');
    }
  };

  const handleDeleteJobSite = (jobSite) => {
    setDeleteModal({
      isOpen: true,
      type: 'jobSite',
      item: jobSite
    });
  };

  const handleDeactivateJobSite = async (jobSite) => {
    try {
      await jobSiteService.deactivateJobSite(jobSite.id);
      loadData();
    } catch (err) {
      console.error('Failed to deactivate:', err);
      alert('Failed to deactivate. Please try again.');
    }
  };

  const handlePermanentDeleteJobSite = async (jobSite) => {
    try {
      await jobSiteService.deleteJobSite(jobSite.id);
      loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
      alert(err?.message || 'Failed to delete. Please try again.');
    }
  };

  const handleReactivateJobSite = async (jobSite) => {
    try {
      await jobSiteService.reactivateJobSite(jobSite.id);
      loadData();
    } catch (err) {
      console.error('Failed to reactivate:', err);
      alert('Failed to reactivate. Please try again.');
    }
  };

  // Filter clients based on search
  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    // Search client name, contact person, and job site names
    const clientMatch =
      client.clientName?.toLowerCase().includes(search) ||
      client.contactPerson?.toLowerCase().includes(search);
    const jobSiteMatch = client.jobSites?.some(site =>
      site.siteName?.toLowerCase().includes(search) ||
      site.address?.toLowerCase().includes(search)
    );
    return clientMatch || jobSiteMatch;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />

      <main className="ml-0 lg:ml-60 mt-[73px] min-h-screen">
        <div className="p-6 lg:p-8">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <Building size={20} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-text-primary">Clients</h1>
                  <p className="text-text-secondary text-sm">
                    Manage your clients and their job sites
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Content Card */}
          <div className="bg-surface border border-border rounded-xl">
            {/* Toolbar */}
            <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-4 justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search clients or job sites..."
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary-orange"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 bg-background text-primary-orange focus:ring-primary-orange"
                  />
                  <span className="text-sm text-text-secondary">Show inactive</span>
                </label>
                <Button onClick={handleAddClient}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </div>
            </div>

            {/* Error State */}
            {error && (
              <div className="p-4 bg-red-500/10 border-b border-red-500/30 text-red-400">
                {error}
              </div>
            )}

            {/* Loading State */}
            {isLoading ? (
              <div className="p-12 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-primary-orange animate-spin" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-surface-light rounded-full flex items-center justify-center">
                  <Building className="w-8 h-8 text-text-tertiary" />
                </div>
                <h3 className="text-lg font-medium text-text-primary mb-2">
                  No clients found
                </h3>
                <p className="text-text-secondary mb-4">
                  {searchTerm
                    ? 'Try adjusting your search'
                    : 'Add your first client to get started'}
                </p>
                {!searchTerm && (
                  <Button onClick={handleAddClient}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Client
                  </Button>
                )}
              </div>
            ) : (
              /* Client List with nested Job Sites */
              <div className="divide-y divide-border">
                {filteredClients.map(client => {
                  const isExpanded = expandedClients.has(client.id);
                  const jobSiteCount = client.jobSites?.length || 0;

                  return (
                    <div key={client.id} className={cn(!client.isActive && 'opacity-60')}>
                      {/* Client Row */}
                      <div
                        className="flex items-center gap-4 px-4 py-3 hover:bg-background/50 transition-colors cursor-pointer"
                        onClick={() => toggleClient(client.id)}
                      >
                        {/* Expand/Collapse Icon */}
                        <div className="w-6 flex justify-center">
                          {jobSiteCount > 0 ? (
                            isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-accent" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-text-secondary" />
                            )
                          ) : (
                            <span className="w-5" />
                          )}
                        </div>

                        {/* Client Icon */}
                        <div className="w-10 h-10 rounded-lg bg-primary-orange/10 flex items-center justify-center flex-shrink-0">
                          <Building className="w-5 h-5 text-primary-orange" />
                        </div>

                        {/* Client Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-text-primary truncate">
                              {client.clientName}
                            </p>
                            {client.tag && (
                              <span className="px-1.5 py-0.5 bg-accent/10 text-accent text-xs rounded font-medium">
                                {client.tag}
                              </span>
                            )}
                            <span className="text-xs text-text-secondary bg-border px-2 py-0.5 rounded">
                              {jobSiteCount} site{jobSiteCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {client.contactPerson && (
                            <p className="text-sm text-text-secondary truncate">{client.contactPerson}</p>
                          )}
                        </div>

                        {/* Contact Info */}
                        <div className="hidden md:flex items-center gap-6">
                          {client.contactEmail && (
                            <div className="flex items-center gap-1.5 text-sm text-text-secondary">
                              <Mail className="w-3.5 h-3.5" />
                              <span className="truncate max-w-[150px]">{client.contactEmail}</span>
                            </div>
                          )}
                          {client.contactPhone && (
                            <div className="flex items-center gap-1.5 text-sm text-text-secondary">
                              <Phone className="w-3.5 h-3.5" />
                              +61 {client.contactPhone}
                            </div>
                          )}
                        </div>

                        {/* Status */}
                        <span className={cn(
                          'hidden sm:inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                          client.isActive
                            ? 'bg-green-500/10 text-green-400'
                            : 'bg-gray-500/10 text-gray-400'
                        )}>
                          {client.isActive ? 'Active' : 'Inactive'}
                        </span>

                        {/* Actions */}
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleAddJobSite(client.id)}
                            className="p-2 text-text-secondary hover:text-accent hover:bg-accent/10 rounded-lg transition-colors"
                            title="Add Job Site"
                          >
                            <MapPinned className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditClient(client)}
                            className="p-2 text-text-secondary hover:text-text-primary hover:bg-background rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {client.isActive ? (
                            <button
                              onClick={() => handleDeleteClient(client)}
                              className="p-2 text-text-secondary hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivateClient(client)}
                              className="p-2 text-text-secondary hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                              title="Reactivate"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Job Sites (nested, shown when expanded) */}
                      {isExpanded && jobSiteCount > 0 && (
                        <div className="bg-background/50 border-t border-border">
                          {client.jobSites.map((jobSite, idx) => (
                            <div
                              key={jobSite.id}
                              className={cn(
                                'flex items-center gap-4 px-4 py-2.5 pl-16 hover:bg-surface/50 transition-colors',
                                !jobSite.isActive && 'opacity-60',
                                idx !== client.jobSites.length - 1 && 'border-b border-border/50'
                              )}
                            >
                              {/* Job Site Icon */}
                              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                                <Building2 className="w-4 h-4 text-accent" />
                              </div>

                              {/* Job Site Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-text-primary text-sm truncate">
                                    {jobSite.siteName}
                                  </p>
                                  {jobSite.siteTag && (
                                    <span className="font-mono text-xs px-1.5 py-0.5 bg-accent/10 text-accent rounded border border-accent/20 flex-shrink-0">
                                      {jobSite.siteTag}
                                    </span>
                                  )}
                                </div>
                                {jobSite.address && (
                                  <div className="flex items-center gap-1 text-xs text-text-secondary truncate">
                                    <MapPin className="w-3 h-3 flex-shrink-0" />
                                    {jobSite.address}
                                  </div>
                                )}
                              </div>

                              {/* Contact Info */}
                              <div className="hidden lg:flex items-center gap-4">
                                {jobSite.contactPerson && (
                                  <span className="text-xs text-text-secondary">
                                    {jobSite.contactPerson}
                                  </span>
                                )}
                                {jobSite.contactPhone && (
                                  <div className="flex items-center gap-1 text-xs text-text-secondary">
                                    <Phone className="w-3 h-3" />
                                    {jobSite.contactPhone}
                                  </div>
                                )}
                              </div>

                              {/* Status */}
                              <span className={cn(
                                'hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                jobSite.isActive
                                  ? 'bg-green-500/10 text-green-400'
                                  : 'bg-gray-500/10 text-gray-400'
                              )}>
                                {jobSite.isActive ? 'Active' : 'Inactive'}
                              </span>

                              {/* Actions */}
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleEditJobSite(jobSite, client.id)}
                                  className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                {jobSite.isActive ? (
                                  <button
                                    onClick={() => handleDeleteJobSite(jobSite)}
                                    className="p-1.5 text-text-secondary hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleReactivateJobSite(jobSite)}
                                    className="p-1.5 text-text-secondary hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                                    title="Reactivate"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Add Job Site inline button */}
                          <div className="px-4 py-2 pl-16 border-t border-border/50">
                            <button
                              onClick={() => handleAddJobSite(client.id)}
                              className="flex items-center gap-2 text-sm text-accent hover:text-accent-600 transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                              Add Job Site
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <DeleteConfirmModal
          isOpen={deleteModal.isOpen}
          type={deleteModal.type}
          item={deleteModal.item}
          onClose={() => setDeleteModal({ isOpen: false, type: null, item: null })}
          onDeactivate={async () => {
            if (deleteModal.type === 'client') {
              await handleDeactivateClient(deleteModal.item);
            } else {
              await handleDeactivateJobSite(deleteModal.item);
            }
            setDeleteModal({ isOpen: false, type: null, item: null });
          }}
          onDelete={async () => {
            if (deleteModal.type === 'client') {
              await handlePermanentDeleteClient(deleteModal.item);
            } else {
              await handlePermanentDeleteJobSite(deleteModal.item);
            }
            setDeleteModal({ isOpen: false, type: null, item: null });
          }}
        />
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <ItemModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingItem(null);
            setSelectedClientId(null);
          }}
          type={modalType}
          item={editingItem}
          clientId={selectedClientId}
          companyId={currentCompany?.id}
          clients={clients}
          onSaved={() => {
            setShowModal(false);
            setEditingItem(null);
            setSelectedClientId(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}

// Add/Edit Modal Component
function ItemModal({ isOpen, onClose, type, item, clientId, companyId, clients, onSaved }) {
  const isClient = type === 'client';
  const isEditing = !!item;

  const [formData, setFormData] = useState({
    // Client fields
    clientName: '',
    // Job Site fields
    siteName: '',
    siteTag: '',
    selectedClientId: clientId || '',
    // Common fields
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    addressLat: null,
    addressLng: null,
    notes: '',
    abn: '',
    tag: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Address update options for job sites
  const [showAddressUpdateOptions, setShowAddressUpdateOptions] = useState(false);
  const [addressUpdateOption, setAddressUpdateOption] = useState('future');

  // Populate form when editing
  useEffect(() => {
    if (item) {
      if (isClient) {
        setFormData({
          clientName: item.clientName || '',
          siteName: '',
          siteTag: '',
          selectedClientId: '',
          contactPerson: item.contactPerson || '',
          contactEmail: item.contactEmail || '',
          contactPhone: item.contactPhone || '',
          address: item.address || '',
          addressLat: item.addressLat || null,
          addressLng: item.addressLng || null,
          notes: item.notes || '',
          abn: item.abn || '',
          tag: item.tag || ''
        });
      } else {
        setFormData({
          clientName: '',
          siteName: item.siteName || '',
          siteTag: item.siteTag || '',
          selectedClientId: clientId || item.clientId || '',
          contactPerson: item.contactPerson || '',
          contactEmail: item.contactEmail || '',
          contactPhone: item.contactPhone || '',
          address: item.address || '',
          addressLat: item.addressLat || null,
          addressLng: item.addressLng || null,
          notes: item.notes || '',
          abn: '',
          tag: ''
        });
      }
    } else {
      setFormData({
        clientName: '',
        siteName: '',
        siteTag: '',
        selectedClientId: clientId || '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
        addressLat: null,
        addressLng: null,
        notes: '',
        abn: '',
        tag: ''
      });
    }
  }, [item, isClient, clientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (isClient && !formData.clientName?.trim()) {
      setError('Client name is required');
      return;
    }
    if (!isClient && !formData.siteName?.trim()) {
      setError('Site name is required');
      return;
    }
    if (!isClient && !formData.address?.trim()) {
      setError('Address is required for job sites');
      return;
    }
    if (!isClient && !formData.selectedClientId) {
      setError('Please select a client for this job site');
      return;
    }

    // Check if address changed for job site edit
    if (!isClient && isEditing && item.address !== formData.address) {
      setShowAddressUpdateOptions(true);
      return;
    }

    await submitForm();
  };

  const submitForm = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      if (isClient) {
        const data = {
          clientName: formData.clientName,
          contactPerson: formData.contactPerson || null,
          contactEmail: formData.contactEmail || null,
          contactPhone: formData.contactPhone || null,
          address: formData.address || null,
          addressLat: formData.addressLat || null,
          addressLng: formData.addressLng || null,
          notes: formData.notes || null,
          abn: formData.abn || null,
          tag: formData.tag || null
        };

        if (isEditing) {
          await clientService.updateClient(item.id, data);
        } else {
          await clientService.createClient(data, companyId);
        }
      } else {
        const data = {
          clientId: formData.selectedClientId,
          siteName: formData.siteName,
          siteTag: formData.siteTag?.trim().toUpperCase() || null,
          contactPerson: formData.contactPerson || null,
          contactEmail: formData.contactEmail || null,
          contactPhone: formData.contactPhone || null,
          address: formData.address,
          addressLat: formData.addressLat || null,
          addressLng: formData.addressLng || null,
          notes: formData.notes || null
        };

        if (isEditing) {
          // Check if address changed
          if (item.address !== formData.address) {
            await jobSiteService.updateJobSiteAddress(
              item.id,
              formData.address,
              formData.addressLat,
              formData.addressLng,
              addressUpdateOption
            );
            // Update other fields
            await jobSiteService.updateJobSite(item.id, {
              ...data,
              address: undefined,
              addressLat: undefined,
              addressLng: undefined
            });
          } else {
            await jobSiteService.updateJobSite(item.id, data);
          }
        } else {
          await jobSiteService.createJobSite(data, companyId);
        }
      }

      onSaved?.();
    } catch (err) {
      console.error('Failed to save:', err);
      setError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
      setShowAddressUpdateOptions(false);
    }
  };

  if (!isOpen) return null;

  // Address Update Options Modal
  if (showAddressUpdateOptions) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
        <div className="bg-background border border-border rounded-xl w-full max-w-md">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-text-primary">
              Update Address in Existing Jobs?
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-sm text-text-secondary">
              You're changing the address for this job site. Would you like to update existing jobs with this address?
            </p>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:border-accent cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="addressUpdate"
                  value="future"
                  checked={addressUpdateOption === 'future'}
                  onChange={() => setAddressUpdateOption('future')}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-text-primary">Future jobs only</p>
                  <p className="text-xs text-text-secondary">Only new jobs will use this address</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:border-accent cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="addressUpdate"
                  value="active"
                  checked={addressUpdateOption === 'active'}
                  onChange={() => setAddressUpdateOption('active')}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-text-primary">Active jobs only</p>
                  <p className="text-xs text-text-secondary">Update jobs that aren't completed or cancelled</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:border-accent cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="addressUpdate"
                  value="all"
                  checked={addressUpdateOption === 'all'}
                  onChange={() => setAddressUpdateOption('all')}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-text-primary">All jobs</p>
                  <p className="text-xs text-text-secondary">Update all jobs for this job site</p>
                </div>
              </label>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowAddressUpdateOptions(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={submitForm}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                'Update Address'
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
      <div className="bg-background border border-border rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">
            {isEditing ? 'Edit' : 'Add'} {isClient ? 'Client' : 'Job Site'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
              {error}
            </div>
          )}

          {/* Client Selector (for Job Sites) */}
          {!isClient && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Client <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.selectedClientId}
                onChange={(e) => setFormData(prev => ({ ...prev, selectedClientId: e.target.value }))}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary-orange"
                disabled={!!clientId} // Disable if pre-selected
              >
                <option value="">Select a client...</option>
                {clients?.filter(c => c.isActive).map(client => (
                  <option key={client.id} value={client.id}>
                    {client.clientName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Client/Site Name */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              {isClient ? 'Client Name' : 'Site Name'} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={isClient ? formData.clientName : formData.siteName}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                [isClient ? 'clientName' : 'siteName']: e.target.value
              }))}
              placeholder={isClient ? 'Acme Corporation' : 'Main Office'}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary-orange"
            />
          </div>

          {/* Site Tag (job sites only) */}
          {!isClient && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Site Tag
                <span className="ml-1.5 text-xs text-text-tertiary font-normal">(max 7 chars, used in inspection IDs)</span>
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                <input
                  type="text"
                  value={formData.siteTag}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
                    setFormData(prev => ({ ...prev, siteTag: value }));
                  }}
                  placeholder="e.g. DHARMA"
                  className="w-full pl-10 pr-3 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary-orange font-mono"
                />
              </div>
            </div>
          )}

          {/* Contact Person */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Contact Person
            </label>
            <input
              type="text"
              value={formData.contactPerson}
              onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e.target.value }))}
              placeholder="John Smith"
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary-orange"
            />
          </div>

          {/* Contact Email & Phone */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                placeholder="email@example.com"
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary-orange"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Phone
              </label>
              <div className="relative flex items-center">
                <span className="absolute left-3 text-text-secondary text-sm">+61</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={formData.contactPhone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setFormData(prev => ({ ...prev, contactPhone: value }));
                  }}
                  placeholder="412 345 678"
                  className="w-full pl-12 pr-3 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary-orange"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Address {!isClient && <span className="text-red-400">*</span>}
            </label>
            <AddressInput
              value={{
                address: formData.address || '',
                lat: formData.addressLat,
                lng: formData.addressLng
              }}
              onChange={({ address, lat, lng }) => {
                setFormData(prev => ({
                  ...prev,
                  address: address || '',
                  addressLat: lat,
                  addressLng: lng
                }));
              }}
              placeholder="Search for address..."
              allowMapToggle={true}
              mapHeight="200px"
            />
          </div>

          {/* ABN and Tag (only for clients) */}
          {isClient && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  ABN
                </label>
                <input
                  type="text"
                  value={formData.abn}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9\s]/g, '');
                    setFormData(prev => ({ ...prev, abn: value }));
                  }}
                  placeholder="12 345 678 901"
                  className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary-orange"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Tag
                </label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                  <input
                    type="text"
                    value={formData.tag}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
                      setFormData(prev => ({ ...prev, tag: value }));
                    }}
                    placeholder="e.g. ACME"
                    className="w-full pl-10 pr-3 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary-orange"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes..."
              rows={3}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary-orange resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Save Changes' : `Add ${isClient ? 'Client' : 'Job Site'}`
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete Confirmation Modal Component
function DeleteConfirmModal({ isOpen, type, item, onClose, onDeactivate, onDelete }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [action, setAction] = useState(null);

  if (!isOpen || !item) return null;

  const isClient = type === 'client';
  const name = isClient ? item.clientName : item.siteName;

  const handleAction = async (actionType) => {
    setIsProcessing(true);
    setAction(actionType);
    try {
      if (actionType === 'deactivate') {
        await onDeactivate();
      } else {
        await onDelete();
      }
    } finally {
      setIsProcessing(false);
      setAction(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
      <div className="bg-background border border-border rounded-xl w-full max-w-md">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">
            Delete {isClient ? 'Client' : 'Job Site'}
          </h2>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-text-secondary">
            What would you like to do with <span className="font-semibold text-text-primary">"{name}"</span>?
          </p>

          {/* Options */}
          <div className="space-y-3">
            {/* Deactivate Option */}
            <button
              onClick={() => handleAction('deactivate')}
              disabled={isProcessing}
              className="w-full p-4 text-left rounded-lg border border-border hover:border-yellow-500/50 hover:bg-yellow-500/5 transition-colors disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                  <RotateCcw className="w-5 h-5 text-yellow-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-text-primary">Deactivate</p>
                  <p className="text-sm text-text-secondary mt-0.5">
                    {isClient
                      ? 'Hide this client and all its job sites. Can be reactivated later from "Show inactive".'
                      : 'Hide this job site. Can be reactivated later from "Show inactive".'}
                  </p>
                </div>
                {isProcessing && action === 'deactivate' && (
                  <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
                )}
              </div>
            </button>

            {/* Delete Permanently Option */}
            <button
              onClick={() => handleAction('delete')}
              disabled={isProcessing}
              className="w-full p-4 text-left rounded-lg border border-border hover:border-red-500/50 hover:bg-red-500/5 transition-colors disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5 text-red-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-red-400">Delete Permanently</p>
                  <p className="text-sm text-text-secondary mt-0.5">
                    {isClient
                      ? 'Permanently remove this client and all its job sites. This cannot be undone.'
                      : 'Permanently remove this job site. This cannot be undone.'}
                  </p>
                  {isClient && item.jobSites?.length > 0 && (
                    <p className="text-xs text-red-400 mt-1">
                      Warning: This will also delete {item.jobSites.length} job site{item.jobSites.length !== 1 ? 's' : ''}.
                    </p>
                  )}
                </div>
                {isProcessing && action === 'delete' && (
                  <Loader2 className="w-5 h-5 animate-spin text-red-500" />
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end">
          <Button variant="ghost" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
